import { motion } from "motion/react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ShoppingCart, Scan } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useNavigate } from "react-router";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../../lib/api";
import { formatInrIntl } from "../../../lib/currency";
import { useAuth } from "../../../providers/AuthProvider";

export type PosQuickAccessSnapshot = {
  todayOrders: number;
  todayRevenueFormatted: string;
};

export function POSQuickAccess({ snapshot }: { snapshot?: PosQuickAccessSnapshot | null }) {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [salesTodayCount, setSalesTodayCount] = useState<number>(0);
  const [todayRevenue, setTodayRevenue] = useState<string>(() => formatInrIntl(0));

  const todayYmd = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  useEffect(() => {
    if (snapshot) {
      setSalesTodayCount(snapshot.todayOrders);
      setTodayRevenue(snapshot.todayRevenueFormatted);
    }
  }, [snapshot]);

  useEffect(() => {
    if (snapshot) return;
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.reports.getSalesSummary({
          token,
          filters: { from_date: todayYmd, to_date: todayYmd },
        });
        if (cancelled) return;

        const totalOrders = Number((res.data as any)?.total_orders ?? 0);
        const totalRevenue = Number((res.data as any)?.total_revenue ?? 0);

        setSalesTodayCount(totalOrders);
        setTodayRevenue(formatInrIntl(totalRevenue));
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, todayYmd, snapshot]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.6 }}
    >
      <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            POS Quick Access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Scan className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-foreground/80" />
            <Input
              type="text"
              placeholder="Scan barcode or search product..."
              className="pl-11 border-primary-foreground/30 bg-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/70 focus:bg-primary-foreground/30"
            />
          </div>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              size="lg"
              onClick={() => navigate("/pos")}
              className="w-full bg-primary-foreground text-primary font-semibold shadow-lg hover:bg-primary-foreground/90"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Start Billing
            </Button>
          </motion.div>
          <div className="grid grid-cols-2 gap-2 border-t border-primary-foreground/20 pt-2 text-center">
            <div>
              <p className="text-2xl font-bold">{salesTodayCount}</p>
              <p className="text-xs text-primary-foreground/80">Sales Today</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{todayRevenue}</p>
              <p className="text-xs text-primary-foreground/80">Today's Revenue</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
